import {
  ProfileTemplatePublishStatus,
  ProfileTemplatesApi,
} from '@up2stay/up2promo-api-client';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { TemplateMemberValueColumn } from '@/core/lib/profile-template-member-values';
import { emitTemplateSyncStarted } from '@/core/lib/profile-template-sync-events';
import { columnToMemberValuesQuery } from '@/core/lib/template-member-value-column-api';

function parseValuesMap(
  raw: Record<string, unknown> | undefined,
): Record<string, string> {
  const next: Record<string, string> = {};
  if (!raw) return next;
  for (const [userId, value] of Object.entries(raw)) {
    next[userId] = typeof value === 'string' ? value : String(value ?? '');
  }
  return next;
}

export function useTemplateColumnMemberValues(params: {
  workspaceId: string | undefined;
  templateId: string | undefined;
  column: TemplateMemberValueColumn;
  publishStatus: ProfileTemplatePublishStatus | undefined;
  open: boolean;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [pendingChangesCount, setPendingChangesCount] = useState(0);
  const pendingUpdatesRef = useRef<Record<string, string>>({});
  const lastLoadedValuesRef = useRef<Record<string, string>>({});

  const canEdit = params.publishStatus !== ProfileTemplatePublishStatus.SYNCING;

  useEffect(() => {
    const workspaceId = params.workspaceId;
    const templateId = params.templateId;
    if (!params.open || !workspaceId || !templateId || !canEdit) {
      return;
    }

    let cancelled = false;

    void (async () => {
      setLoading(true);
      setLoadFailed(false);

      try {
        const res = await ProfileTemplatesApi.getMemberValues({
          path: {
            workspaceId,
            templateId,
          },
          query: columnToMemberValuesQuery(params.column),
        });
        if (cancelled) return;
        const nextValues = parseValuesMap(
          res.data?.values as Record<string, unknown>,
        );
        setValues(nextValues);
        lastLoadedValuesRef.current = nextValues;
        pendingUpdatesRef.current = {};
        setHasPendingChanges(false);
        setPendingChangesCount(0);
        setSaveFailed(false);
      } catch {
        if (!cancelled) setLoadFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    canEdit,
    params.column,
    params.open,
    params.templateId,
    params.workspaceId,
  ]);

  const commitChanges = useCallback(async () => {
    const updates = { ...pendingUpdatesRef.current };
    if (
      !params.workspaceId ||
      !params.templateId ||
      Object.keys(updates).length === 0
    ) {
      return true;
    }

    setSaving(true);
    setSaveFailed(false);
    try {
      await ProfileTemplatesApi.setMemberValues({
        path: {
          workspaceId: params.workspaceId,
          templateId: params.templateId,
        },
        body: {
          column: columnToMemberValuesQuery(params.column),
          updates,
        },
      });
      emitTemplateSyncStarted({
        workspaceId: params.workspaceId,
        templateId: params.templateId,
        reason: 'memberValues',
      });
      pendingUpdatesRef.current = {};
      lastLoadedValuesRef.current = values;
      setHasPendingChanges(false);
      setPendingChangesCount(0);
      return true;
    } catch {
      setSaveFailed(true);
      return false;
    } finally {
      setSaving(false);
    }
  }, [params.column, params.templateId, params.workspaceId, values]);

  const discardChanges = useCallback(() => {
    pendingUpdatesRef.current = {};
    setValues(lastLoadedValuesRef.current);
    setHasPendingChanges(false);
    setPendingChangesCount(0);
    setSaveFailed(false);
  }, []);

  const updateCell = useCallback(
    (userId: string, value: string, normalizeWhitespace = false) => {
      const stored = normalizeWhitespace ? value.trim() : value;
      const apiValue = stored.trim() === '' ? '' : stored;

      setValues((prev) => {
        const next = { ...prev };
        // Keep explicit empty-string edits in local state so "leave empty"
        // does not visually snap back to template default in the editor.
        next[userId] = apiValue;
        return next;
      });

      pendingUpdatesRef.current[userId] = apiValue;
      setHasPendingChanges(true);
      setPendingChangesCount(Object.keys(pendingUpdatesRef.current).length);
      setSaveFailed(false);
    },
    [],
  );

  const applyBulkUpdates = useCallback((updates: Record<string, string>) => {
    if (Object.keys(updates).length === 0) return;

    const normalizedUpdates: Record<string, string> = {};
    setValues((prev) => {
      const next = { ...prev };
      for (const [userId, value] of Object.entries(updates)) {
        const trimmed = value.trim();
        const normalized = trimmed === '' ? '' : trimmed;
        next[userId] = normalized;
        normalizedUpdates[userId] = normalized;
      }
      return next;
    });

    pendingUpdatesRef.current = {
      ...pendingUpdatesRef.current,
      ...normalizedUpdates,
    };
    setHasPendingChanges(true);
    setPendingChangesCount(Object.keys(pendingUpdatesRef.current).length);
    setSaveFailed(false);
  }, []);

  return {
    values,
    loading,
    saving,
    loadFailed,
    saveFailed,
    hasPendingChanges,
    pendingChangesCount,
    canEdit,
    updateCell,
    applyBulkUpdates,
    commitChanges,
    discardChanges,
  };
}
